import { supabase, db } from '@/lib/supabase'
import { appFetch, isAppApiConfigured } from '@/lib/appApi'
import { buildOrIlikeClause } from '@/lib/dashboard-v2/postgrestOrIlike'
import { DASHBOARD_EXPORT_MAX_ROWS, type DashboardExportResult } from '@/lib/dashboard-v2/dashboardExportLimits'

// Types
export interface Customer {
    id: string
    user_id: string
    store_id: string
    name: string
    email: string | null
    phone: string
    city: string | null
    address: string | null
    total_orders: number
    total_spent: number
    first_order_at: string | null
    last_order_at: string | null
    notes: string | null
    tags: string[]
    status: 'active' | 'inactive' | 'blocked'
    created_at: string
    updated_at: string
}

export interface CustomerInteraction {
    id: string
    user_id: string
    customer_id: string
    type: 'call' | 'email' | 'sms' | 'whatsapp' | 'note' | 'order'
    subject: string | null
    content: string | null
    direction: 'inbound' | 'outbound' | null
    order_id: string | null
    metadata: any
    created_at: string
    updated_at: string
}

export type CustomerInsert = Pick<Customer, 'name' | 'phone'> & Partial<Pick<Customer, 'email' | 'city' | 'notes' | 'tags' | 'status'>>
export type CustomerUpdate = Partial<Pick<Customer, 'name' | 'email' | 'phone' | 'city' | 'notes' | 'tags' | 'status'>>

const MAX_CUSTOMER_MATCHING_IDS = 10_000

export type CustomerListPageResult = { rows: Customer[]; totalCount: number }

// Customers Service
export const customersService = {
    async getAll(storeId: string): Promise<Customer[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const out: Customer[] = []
            let page = 1
            const batch = 100
            while (true) {
                const { rows, totalCount } = await customersService.listPage(storeId, {
                    page,
                    pageSize: batch,
                    q: '',
                    sortKey: null,
                    sortDir: 'desc',
                })
                out.push(...rows)
                if (out.length >= totalCount || rows.length === 0) break
                page += 1
                if (page > 200) break
            }
            return out
        }

        const { data, error } = await db
            .from('customers')
            .select('*')
            .eq('store_id', storeId)
            .order('last_order_at', { ascending: false, nullsFirst: false })

        if (error) throw error
        return data || []
    },

    async listPage(
        storeId: string,
        opts: {
            page: number
            pageSize: number
            q: string
            sortKey: string | null
            sortDir: 'asc' | 'desc'
        }
    ): Promise<CustomerListPageResult> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        const page = Math.max(1, Math.floor(opts.page))
        const pageSize = Math.min(20, Math.max(1, Math.floor(opts.pageSize)))

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({
                storeId,
                page: String(page),
                pageSize: String(pageSize),
                q: opts.q ?? '',
                sortKey: opts.sortKey ?? '',
                sortDir: opts.sortDir,
            })
            const j = await appFetch<{ ok: true; rows: Customer[]; totalCount: number }>(
                `/api/app/customers?${params.toString()}`
            )
            if (!j || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return { rows: j.rows, totalCount: j.totalCount ?? 0 }
        }

        const from = (page - 1) * pageSize
        const to = from + pageSize - 1

        let query = db.from('customers').select('*', { count: 'exact' }).eq('store_id', storeId)

        const orClause = buildOrIlikeClause(['name', 'email', 'phone'], opts.q || '')
        if (orClause) query = query.or(orClause)

        const sortKey = opts.sortKey || ''
        const asc = opts.sortDir === 'asc'
        if (sortKey === 'customer') query = query.order('name', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'contact') query = query.order('phone', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'orders') query = query.order('total_orders', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'ltv') query = query.order('total_spent', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'last_order') query = query.order('last_order_at', { ascending: asc, nullsFirst: false })
        else query = query.order('last_order_at', { ascending: false, nullsFirst: false })

        const { data, error, count } = await query.range(from, to)
        if (error) throw error
        return { rows: (data || []) as Customer[], totalCount: count ?? 0 }
    },

    async listMatchingIds(storeId: string, opts: { q: string }): Promise<string[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, q: opts.q ?? '' })
            const j = await appFetch<{ ok: true; ids: string[] }>(`/api/app/customers/matching-ids?${params.toString()}`)
            if (!j || !Array.isArray((j as { ids?: unknown }).ids)) {
                throw new Error('Invalid API response')
            }
            return (j as { ids: string[] }).ids
        }

        let query = db.from('customers').select('id').eq('store_id', storeId).limit(MAX_CUSTOMER_MATCHING_IDS)
        const orClause = buildOrIlikeClause(['name', 'email', 'phone'], opts.q || '')
        if (orClause) query = query.or(orClause)
        const { data, error } = await query.order('last_order_at', { ascending: false, nullsFirst: false })
        if (error) throw error
        return (data || []).map((r: { id: string }) => r.id)
    },

    async fetchByIds(storeId: string, ids: string[]): Promise<Customer[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')
        if (ids.length === 0) return []

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; rows: Customer[] }>('/api/app/customers/fetch-ids', {
                method: 'POST',
                json: { storeId, ids },
            })
            if (!j || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Customer[] }).rows
        }

        const CHUNK = 200
        const out: Customer[] = []
        for (let i = 0; i < ids.length; i += CHUNK) {
            const slice = ids.slice(i, i + CHUNK)
            const { data, error } = await db
                .from('customers')
                .select('*')
                .eq('store_id', storeId)
                .in('id', slice)
            if (error) throw error
            out.push(...((data || []) as Customer[]))
        }
        return out
    },

    async fetchAllMatchingForExport(storeId: string, opts: { q: string }): Promise<DashboardExportResult<Customer>> {
        const out: Customer[] = []
        const batch = 500
        let page = 1
        while (true) {
            const { rows, totalCount } = await customersService.listPage(storeId, {
                page,
                pageSize: batch,
                q: opts.q,
                sortKey: null,
                sortDir: 'desc',
            })
            out.push(...rows)
            if (out.length >= DASHBOARD_EXPORT_MAX_ROWS) {
                return { rows: out.slice(0, DASHBOARD_EXPORT_MAX_ROWS), truncated: true }
            }
            if (out.length >= totalCount || rows.length === 0) break
            page += 1
            if (page > 200) break
        }
        return { rows: out, truncated: false }
    },

    async getById(id: string): Promise<Customer | null> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; customer: Customer | null }>(
                `/api/app/customers/${encodeURIComponent(id)}`
            )
            if (!j || !('customer' in j)) throw new Error('Invalid API response')
            return (j as { customer: Customer | null }).customer
        }

        const { data, error } = await db
            .from('customers')
            .select('*')
            .eq('id', id)
            .single()

        if (error) {
            if (error.code === 'PGRST116') return null
            throw error
        }
        return data
    },

    async getByPhone(phone: string, storeId: string): Promise<Customer | null> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, phone })
            const j = await appFetch<{ ok: true; customer: Customer | null }>(
                `/api/app/customers/lookup/phone?${params.toString()}`
            )
            if (!j || !('customer' in j)) throw new Error('Invalid API response')
            return (j as { customer: Customer | null }).customer
        }

        const { data, error } = await db
            .from('customers')
            .select('*')
            .eq('phone', phone)
            .eq('store_id', storeId)
            .single()

        if (error) {
            if (error.code === 'PGRST116') return null
            throw error
        }
        return data
    },

    async create(storeId: string, customer: CustomerInsert): Promise<Customer> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; customer: Customer }>('/api/app/customers', {
                method: 'POST',
                json: { storeId, ...customer },
            })
            if (!j?.customer) throw new Error('Invalid API response')
            return j.customer
        }

        const { data, error } = await db
            .from('customers')
            .insert({
                ...customer,
                user_id: user.id,
                store_id: storeId
            })
            .select()
            .single()

        if (error) throw error
        return data
    },

    async update(id: string, updates: CustomerUpdate): Promise<Customer> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; customer: Customer }>(`/api/app/customers/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                json: updates,
            })
            if (!j?.customer) throw new Error('Invalid API response')
            return j.customer
        }

        const { data, error } = await db
            .from('customers')
            .update(updates)
            .eq('id', id)
            .select()
            .single()

        if (error) throw error
        return data
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            await appFetch<{ ok: true }>(`/api/app/customers/${encodeURIComponent(id)}`, { method: 'DELETE' })
            return
        }

        const { error } = await db
            .from('customers')
            .delete()
            .eq('id', id)

        if (error) throw error
    },

    async search(query: string, storeId: string): Promise<Customer[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const { rows } = await customersService.listPage(storeId, {
                page: 1,
                pageSize: 500,
                q: query,
                sortKey: null,
                sortDir: 'desc',
            })
            return rows
        }

        const orClause = buildOrIlikeClause(['name', 'email', 'phone'], query)
        let qb = db.from('customers').select('*').eq('store_id', storeId)
        if (orClause) qb = qb.or(orClause)
        const { data, error } = await qb.order('last_order_at', { ascending: false, nullsFirst: false })

        if (error) throw error
        return data || []
    },

    async getStats(storeId: string): Promise<{
        total: number
        totalSpent: number
        averageOrderValue: number
    }> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{
                ok: true
                data: { total: number; totalSpent: number; averageOrderValue: number }
            }>(`/api/app/customers/stats?storeId=${encodeURIComponent(storeId)}`)
            return j.data
        }

        const { data: rows, error } = await db
            .from('customers')
            .select('total_spent, total_orders')
            .eq('store_id', storeId)

        if (error) throw error

        const total = rows?.length ?? 0
        const totalSpent = rows?.reduce((a, r) => a + Number(r.total_spent || 0), 0) ?? 0
        const orderCountSum = rows?.reduce((a, r) => a + Number(r.total_orders || 0), 0) ?? 0
        const averageOrderValue = orderCountSum > 0 ? totalSpent / orderCountSum : 0

        return { total, totalSpent, averageOrderValue }
    },

    async getTop(limit: number = 10, storeId?: string): Promise<Customer[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')
        if (!storeId) return []

        if (isAppApiConfigured()) {
            const { rows } = await customersService.listPage(storeId, {
                page: 1,
                pageSize: limit,
                q: '',
                sortKey: 'ltv',
                sortDir: 'desc',
            })
            return rows
        }

        const query = db
            .from('customers')
            .select('*')
            .eq('store_id', storeId)

        const { data, error } = await query
            .order('total_spent', { ascending: false })
            .limit(limit)

        if (error) throw error
        return data || []
    },

    async getInteractions(customerId: string): Promise<CustomerInteraction[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; rows: CustomerInteraction[] }>(
                `/api/app/customers/${encodeURIComponent(customerId)}/interactions`
            )
            return j.rows ?? []
        }

        const { data, error } = await db
            .from('customer_interactions')
            .select('*')
            .eq('customer_id', customerId)
            .order('created_at', { ascending: false })

        if (error) throw error
        return data || []
    }
}
